'use client';

import type { StoreHit } from '@/lib/algolia/stores';
import { useCallback, useState } from 'react';

import { getTranslatedStoreValue } from './utils';

export interface RegionCityOption {
  label: string;
  value: string;
}

export interface RetailerOption {
  label: string;
  value: string;
  data: StoreHit;
}

export interface BoutiqueSelectState {
  region: RegionCityOption | null;
  city: RegionCityOption | null;
  regionOptions: RegionCityOption[];
  cityOptions: RegionCityOption[];
  boutiqueList: StoreHit[];
  boutiqueOptions: RetailerOption[];
  boutique: RetailerOption | null;
}

const INITIAL_STATE: BoutiqueSelectState = {
  region: null,
  city: null,
  regionOptions: [],
  cityOptions: [],
  boutiqueList: [],
  boutiqueOptions: [],
  boutique: null,
};

function sortByLabel(options: RegionCityOption[]): RegionCityOption[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
}

function uniqueByValue(options: RegionCityOption[]): RegionCityOption[] {
  const seen = new Set<string>();
  return options.filter((opt) => {
    if (!opt.value || seen.has(opt.value)) return false;
    seen.add(opt.value);
    return true;
  });
}

/**
 * Build region dropdown options from store search results.
 *
 * Example Input:
 * hits=[{ region: "IL" }], allRegionsLabel="All regions"
 *
 * Example Output:
 * [{ label: "All regions", value: "" }, { label: "IL", value: "IL" }]
 */
export function makeRegionOptions(hits: StoreHit[], allRegionsLabel: string): RegionCityOption[] {
  const opts = uniqueByValue(
    hits
      .map((item) => ({
        label: item.region ?? '',
        value: item.region ?? '',
      }))
      .filter((opt) => opt.label.trim() !== '')
  );

  const sorted = sortByLabel(opts);
  if (sorted.length > 0) {
    sorted.unshift({ label: allRegionsLabel, value: '' });
  }
  return sorted;
}

/**
 * Build country dropdown options for the unified appointment page.
 *
 * Example Input:
 * countries=[{ code: "IT", label: "Italy" }]
 *
 * Example Output:
 * [{ label: "Italy", value: "IT" }]
 */
export function makeCountryOptions(
  countries: Array<{ code: string; label: string }>
): RegionCityOption[] {
  return sortByLabel(
    countries.map((country) => ({
      label: country.label,
      value: country.code.toUpperCase(),
    }))
  );
}

/**
 * Build city dropdown options from a filtered store list.
 *
 * Example Input:
 * stores=[{ city: "Milano", city_it: "Milano" }], localeSlug="it"
 *
 * Example Output:
 * [{ label: "Milano", value: "Milano" }]
 */
export function makeCityOptions(stores: StoreHit[], localeSlug?: string): RegionCityOption[] {
  const cityOpts = stores.map((item) => {
    const value = item.city;
    const label = localeSlug ? getTranslatedStoreValue(item, 'city', localeSlug) : item.city;

    return { label, value };
  });

  const unique = uniqueByValue(cityOpts);
  return sortByLabel(unique);
}

/**
 * Map store hits to retailer select options, disambiguating duplicate names.
 *
 * Example Input:
 * searchResults=[{ store_id: "1", name: "Boutique" }], localeSlug="en"
 *
 * Example Output:
 * [{ label: "Boutique", value: "1", data: { store_id: "1", name: "Boutique" } }]
 */
export function getRetailerOptsFromSearchResults(
  searchResults: StoreHit[],
  localeSlug: string
): RetailerOption[] {
  const seen = new Set<string>();
  const retailerList: RetailerOption[] = [];

  for (const item of searchResults) {
    const value = item.store_id ?? '';
    if (!value || seen.has(value)) continue;
    seen.add(value);

    retailerList.push({
      label: getTranslatedStoreValue(item, 'name', localeSlug),
      value,
      data: item,
    });
  }

  const labelCounts = new Map<string, number>();
  for (const item of retailerList) {
    labelCounts.set(item.label, (labelCounts.get(item.label) ?? 0) + 1);
  }

  return retailerList.map((item) => {
    if ((labelCounts.get(item.label) ?? 0) > 1) {
      return {
        ...item,
        label: `${item.label} ${getTranslatedStoreValue(item.data, 'street', localeSlug)}`,
      };
    }
    return item;
  });
}

export interface AppointmentRetailerSelectState {
  country: RegionCityOption | null;
  city: RegionCityOption | null;
  countryOptions: RegionCityOption[];
  cityOptions: RegionCityOption[];
  boutiqueList: StoreHit[];
  boutiqueOptions: RetailerOption[];
  boutique: RetailerOption | null;
}

const APPOINTMENT_INITIAL_STATE: AppointmentRetailerSelectState = {
  country: null,
  city: null,
  countryOptions: [],
  cityOptions: [],
  boutiqueList: [],
  boutiqueOptions: [],
  boutique: null,
};

/**
 * Manage country / city / boutique selection for the unified appointment page.
 *
 * Example Input:
 * localeSlug="it"
 *
 * Example Output:
 * { retailerSelectState, handleChangeCountry, handleChangeCity, ... }
 */
export function useAppointmentRetailerSelect(localeSlug: string) {
  const [retailerSelectState, setRetailerSelectState] =
    useState<AppointmentRetailerSelectState>(APPOINTMENT_INITIAL_STATE);

  const handleChangeCountry = useCallback((opt: RegionCityOption) => {
    setRetailerSelectState((prev) => ({
      ...prev,
      country: opt,
      city: null,
      boutique: null,
      cityOptions: [],
      boutiqueOptions: [],
      boutiqueList: [],
    }));
  }, []);

  const handleChangeCity = useCallback(
    (opt: RegionCityOption) => {
      setRetailerSelectState((prev) => {
        const stores = opt.value
          ? prev.boutiqueList.filter((item) => item.city === opt.value)
          : prev.boutiqueList;

        return {
          ...prev,
          city: opt,
          boutique: null,
          boutiqueOptions: getRetailerOptsFromSearchResults(stores, localeSlug),
        };
      });
    },
    [localeSlug]
  );

  const handleChangeBoutique = useCallback((opt: RetailerOption) => {
    setRetailerSelectState((prev) => ({
      ...prev,
      boutique: opt,
    }));
  }, []);

  const resetSelection = useCallback(() => {
    setRetailerSelectState((prev) => ({
      ...prev,
      city: null,
      boutique: null,
      boutiqueOptions: [],
    }));
  }, []);

  return {
    retailerSelectState,
    setRetailerSelectState,
    handleChangeCountry,
    handleChangeCity,
    handleChangeBoutique,
    resetSelection,
  };
}

/**
 * Manage cascading region/city/boutique selection for appointment booking.
 *
 * Example Input:
 * localeSlug="en"
 *
 * Example Output:
 * { boutiqueSelectState, handleChangeRegion, handleChangeCity, setBoutiqueSelectState, resetSelection }
 */
export function useBoutiqueSelect(localeSlug: string) {
  const [boutiqueSelectState, setBoutiqueSelectState] =
    useState<BoutiqueSelectState>(INITIAL_STATE);

  const handleChangeRegion = useCallback(
    (opt: RegionCityOption) => {
      setBoutiqueSelectState((prev) => {
        const stores = opt.value
          ? prev.boutiqueList.filter((item) => item.region === opt.value)
          : prev.boutiqueList;

        return {
          ...prev,
          region: opt,
          city: null,
          boutique: null,
          cityOptions: makeCityOptions(stores),
          boutiqueOptions: getRetailerOptsFromSearchResults(stores, localeSlug),
        };
      });
    },
    [localeSlug]
  );

  const handleChangeCity = useCallback(
    (opt: RegionCityOption) => {
      setBoutiqueSelectState((prev) => {
        const baseStores = prev.region?.value
          ? prev.boutiqueList.filter((item) => item.region === prev.region?.value)
          : prev.boutiqueList;

        const stores = opt.value
          ? baseStores.filter((item) => item.city === opt.value)
          : baseStores;

        return {
          ...prev,
          city: opt,
          boutique: null,
          boutiqueOptions: getRetailerOptsFromSearchResults(stores, localeSlug),
        };
      });
    },
    [localeSlug]
  );

  const handleChangeBoutique = useCallback((opt: RetailerOption) => {
    setBoutiqueSelectState((prev) => ({
      ...prev,
      boutique: opt,
    }));
  }, []);

  const resetSelection = useCallback(() => {
    setBoutiqueSelectState((prev) => ({
      ...prev,
      region: null,
      city: null,
      boutique: null,
    }));
  }, []);

  return {
    boutiqueSelectState,
    setBoutiqueSelectState,
    handleChangeRegion,
    handleChangeCity,
    handleChangeBoutique,
    resetSelection,
  };
}
